import type { TFunction, i18n as I18n } from "i18next";

/**
 * Parse a notification's TitleParams/BodyParams JSON and localize any param VALUE that is itself a
 * `notifications.*` translation key (e.g. Q12NudgeJob's `message: "notifications.q12NudgeDigest.message.Q4"`).
 *
 * Why: the server's `Locales.t` (Services/Utils/Locales.cs) already translates a param value that is a
 * key when it renders push, email and the notification-list endpoint. The realtime SignalR payload
 * ("NotificationReceived") carries the RAW key + params, and i18next never translates interpolation
 * values, so without this the popup printed the key itself. Restricted to the `notifications.` prefix
 * so data values (department names, people) are never re-interpreted as keys on the client.
 */
export function localizeNotificationParams(
  raw: string | undefined | null,
  t: TFunction,
  i18n: Pick<I18n, "exists">,
): Record<string, unknown> {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object") return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    out[key] =
      typeof value === "string" && value.startsWith("notifications.") && i18n.exists(value, { nsSeparator: false })
        ? t(value, { nsSeparator: false })
        : value;
  }
  return out;
}
